import { z } from "zod";

import { REGISTER_LIMITS, REGISTER_PASSWORD_REGEX } from "../constants/register.constants";

/** Max lengths from the backend `AcceptInvitationDto`. */
export const INVITATION_LIMITS = {
  NAME_MAX: 255,
  PASSWORD_MAX: 128,
} as const;

export interface AcceptInvitationSchemaTranslator {
  (key: "name_required" | "name_max" | "required" | "min" | "max" | "number" | "symbol" | "match"): string;
}

/**
 * Accept-invitation form schema — password rules mirror the backend `AcceptInvitationDto` exactly (min 8, max
 * 128, one number, one symbol) and the confirm field follows the house confirm-password pattern
 * (`createResetPasswordSchema`): required + `.refine` equality with the error attached to `confirmPassword`.
 * `fullName` is required only when the invitation did not carry a name (the backend falls back to the
 * invitation's own `full_name` otherwise).
 */
export const createAcceptInvitationSchema = (t: AcceptInvitationSchemaTranslator, nameRequired: boolean) =>
  z
    .object({
      fullName: z
        .string()
        .trim()
        .max(INVITATION_LIMITS.NAME_MAX, t("name_max"))
        .refine((value) => !nameRequired || value.length > 0, t("name_required")),
      password: z
        .string()
        .min(1, t("required"))
        .min(REGISTER_LIMITS.PASSWORD_MIN, t("min"))
        .max(INVITATION_LIMITS.PASSWORD_MAX, t("max"))
        .regex(REGISTER_PASSWORD_REGEX.HAS_NUMBER, t("number"))
        .regex(REGISTER_PASSWORD_REGEX.HAS_SYMBOL, t("symbol")),
      confirmPassword: z.string().min(1, t("required")),
    })
    .refine((data) => data.password === data.confirmPassword, {
      path: ["confirmPassword"],
      message: t("match"),
    });

export type AcceptInvitationFormValues = z.infer<ReturnType<typeof createAcceptInvitationSchema>>;
